import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Calendar,
  Users,
  Trophy,
  Plus,
  ArrowLeft,
  Edit,
  CirclePlus,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getLeague, listEvents } from "../../graphql/queries";
import { formatDate, isToday, isPast } from "../../utils/dateUtils";
import { getLeagueGameLabel } from "../../lib/leagueGames";
import { getLeagueScoring } from "../../lib/scoring";

const client = generateClient();

const LeagueDetail: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<
    "upcoming" | "past" | "completed" | "all"
  >("upcoming");

  const {
    data: league,
    isLoading: leagueLoading,
    error: leagueError,
  } = useQuery({
    queryKey: ["league", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getLeague,
        variables: { id: leagueId! },
      });
      return result.data.getLeague;
    },
    enabled: !!leagueId,
  });

  const {
    data: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["events", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: {
          filter: { leagueId: { eq: leagueId } },
          limit: 100,
        },
      });
      return result.data.listEvents.items;
    },
    enabled: !!leagueId,
  });

  const getStatusBadge = (
    isActive: boolean,
    endDate: string | null | undefined
  ) => {
    if (!isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (endDate && new Date(endDate) < new Date()) {
      return <Badge variant="destructive">Ended</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  // Filter and sort events based on active filter
  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let filtered = events;

    switch (activeFilter) {
      case "upcoming":
        filtered = events.filter((event: any) => {
          return !isPast(event.date) && !event.isCompleted;
        });
        break;
      case "past":
        filtered = events.filter((event: any) => {
          return isPast(event.date);
        });
        break;
      case "completed":
        filtered = events.filter((event: any) => event.isCompleted);
        break;
      case "all":
        filtered = events;
        break;
    }

    // Sort by date (most recent first)
    return filtered.sort(
      (a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [events, activeFilter]);

  if (leagueLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (leagueError) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading league: {leagueError.message}
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">League not found</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/leagues")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leagues
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{league.name}</h1>
          <p className="text-muted-foreground mt-2">
            League details and event management
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/admin/leagues/${leagueId}/point-adjustments`)
            }
          >
            <CirclePlus className="h-4 w-4 mr-2" />
            Point Adjustments
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/leagues/${leagueId}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit League
          </Button>
          <Button
            onClick={() =>
              navigate(`/admin/events/create?leagueId=${leagueId}`)
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* League Details */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                League Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status</span>
                {getStatusBadge(league.isActive, league.endDate)}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Game</span>
                <Badge variant="secondary">
                  {getLeagueGameLabel(league.game)}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Format</span>
                <span className="text-sm text-muted-foreground">
                  {league.format}
                </span>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <p className="mb-2 text-sm font-medium">Point allocation</p>
                <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                  <span>Participation</span>
                  <span className="text-right">
                    +{getLeagueScoring(league).participationPoints}
                  </span>
                  {league.format === "Commander" ? (
                    <>
                      <span>Pod placements 1st–4th</span>
                      <span className="text-right">
                        {getLeagueScoring(league).commanderFirstPoints} /{" "}
                        {getLeagueScoring(league).commanderSecondPoints} /{" "}
                        {getLeagueScoring(league).commanderThirdPoints} /{" "}
                        {getLeagueScoring(league).commanderFourthPoints}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>Win / Draw / Loss</span>
                      <span className="text-right">
                        {getLeagueScoring(league).winPoints} /{" "}
                        {getLeagueScoring(league).drawPoints} /{" "}
                        {getLeagueScoring(league).lossPoints}
                      </span>
                      <span>Top 8 tiers</span>
                      <span className="text-right">
                        {getLeagueScoring(league).top8FirstPoints} /{" "}
                        {getLeagueScoring(league).top8SecondPoints} /{" "}
                        {getLeagueScoring(league).top8ThirdFourthPoints} /{" "}
                        {getLeagueScoring(league).top8FifthEighthPoints}
                      </span>
                    </>
                  )}
                  <span>Premium</span>
                  <span className="text-right">
                    {getLeagueScoring(league).premiumMultiplier}x
                  </span>
                </div>
              </div>

              {league.description && (
                <div>
                  <span className="text-sm font-medium">Description</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    {league.description}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {formatDate(league.startDate)}
                    {league.endDate && ` - ${formatDate(league.endDate)}`}
                  </span>
                </div>

                {league.maxParticipants && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4" />
                    <span>Max: {league.maxParticipants} players</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Events */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Events
              </CardTitle>
              <CardDescription>
                Tournament events within this league
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : eventsError ? (
                <div className="text-center text-red-600 py-8">
                  Error loading events: {eventsError.message}
                </div>
              ) : events && events.length > 0 ? (
                <>
                  {/* Event Summary and Filters */}
                  <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium">
                          Total Events: {events.length}
                        </span>
                        <span className="text-green-600">
                          Upcoming:{" "}
                          {
                            events.filter(
                              (e: any) => !isPast(e.date) && !e.isCompleted
                            ).length
                          }
                        </span>
                        <span className="text-gray-600">
                          Past:{" "}
                          {events.filter((e: any) => isPast(e.date)).length}
                        </span>
                        <span className="text-blue-600">
                          Completed:{" "}
                          {events.filter((e: any) => e.isCompleted).length}
                        </span>
                      </div>
                    </div>

                    {/* Filter Buttons */}
                    <div className="flex items-center gap-2">
                      <span className="mr-2 text-sm font-medium text-muted-foreground">
                        Filter:
                      </span>
                      <Button
                        variant={
                          activeFilter === "upcoming" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setActiveFilter("upcoming")}
                      >
                        Upcoming
                      </Button>
                      <Button
                        variant={
                          activeFilter === "past" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setActiveFilter("past")}
                      >
                        Past
                      </Button>
                      <Button
                        variant={
                          activeFilter === "completed" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setActiveFilter("completed")}
                      >
                        Completed
                      </Button>
                      <Button
                        variant={activeFilter === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveFilter("all")}
                      >
                        All Events
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {filteredEvents.length > 0 ? (
                      filteredEvents.map((event: any) => {
                        const eventIsPast = isPast(event.date);
                        const eventIsToday = isToday(event.date);

                        return (
                          <div
                            key={event.id}
                            className={`flex items-center justify-between rounded-xl border border-white/10 p-4 ${
                              eventIsPast
                                ? "bg-white/[0.02]"
                                : "bg-white/[0.05]"
                            }`}
                          >
                            <div className="flex-1">
                              <h4 className="font-medium text-primary">
                                {event.name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {event.format} • {formatDate(event.date)}
                                {eventIsToday && (
                                  <span className="ml-2 text-blue-600 font-medium">
                                    (Today)
                                  </span>
                                )}
                                {eventIsPast && (
                                  <span className="ml-2 text-gray-500">
                                    (Past)
                                  </span>
                                )}
                              </p>
                              {event.location && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  📍 {event.location}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  event.isCompleted
                                    ? "default"
                                    : eventIsPast
                                      ? "secondary"
                                      : "default"
                                }
                              >
                                {event.isCompleted
                                  ? "Completed"
                                  : eventIsPast
                                    ? "Past"
                                    : "Upcoming"}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(`/admin/events/${event.id}`)
                                }
                              >
                                View
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">
                          No {activeFilter} events
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {activeFilter === "upcoming"
                            ? "No upcoming events in this league"
                            : activeFilter === "past"
                              ? "No past events in this league"
                              : activeFilter === "completed"
                                ? "No completed events in this league"
                                : "No events found"}
                        </p>
                        {activeFilter !== "all" && (
                          <Button
                            variant="outline"
                            onClick={() => setActiveFilter("all")}
                          >
                            View All Events
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Events Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create the first event for this league
                  </p>
                  <Button
                    onClick={() =>
                      navigate(`/admin/events/create?leagueId=${leagueId}`)
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Event
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LeagueDetail;
